<template>
  <div class="znsHistory">
    <h2 class="mb-4">Lịch sử gửi ZNS</h2>

    <!-- FILTER -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <label class="font-weight-bold">Trạng thái batch</label>
            <b-form-select v-model="filter.status" :options="statusOptions" />
          </div>
          <div class="col-md-3">
            <label class="font-weight-bold">Từ ngày</label>
            <input type="date" class="form-control" v-model="filter.fromDate" />
          </div>
          <div class="col-md-3">
            <label class="font-weight-bold">Đến ngày</label>
            <input type="date" class="form-control" v-model="filter.toDate" />
          </div>
        </div>

        <div class="mt-3 d-flex" style="gap:10px">
          <b-button variant="primary" @click="search">
            <i class="fa fa-search mr-1"></i> Tìm kiếm
          </b-button>
          <b-button variant="outline-success" @click="clear">
            <i class="fa fa-refresh mr-1"></i> Đặt lại
          </b-button>
        </div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="table-responsive">
      <b-table hover bordered :items="table" :fields="fields" head-variant="dark">
        <!-- PROGRESS -->
        <template #cell(progress)="d">
          <b-progress :max="d.item.total_count || 1" :value="d.item.processed_count" height="18px">
            <b-progress-bar :value="d.item.processed_count" :max="d.item.total_count || 1">
              {{ d.item.processed_count }} / {{ d.item.total_count }}
            </b-progress-bar>
          </b-progress>
        </template>

        <!-- CREATED TIME -->
        <template #cell(created_at)="d">
          {{ formatDate(d.item.created_at) }}
        </template>

        <!-- ACTIONS -->
        <template #cell(actions)="d">
          <b-button size="sm" variant="link" class="icon-btn text-info" title="Xem log" @click="viewLogs(d.item.id)">
            <i class="fa fa-eye"></i>
          </b-button>
        </template>
      </b-table>

      <div v-if="table.length === 0" class="text-center text-muted mt-3">
        Không có dữ liệu
      </div>
    </div>

    <!-- PAGINATION -->
    <div class="d-flex justify-content-between mt-3 flex-wrap" v-if="total > 0">
      <small class="text-muted">
        Hiển thị {{ (page - 1) * perPage + 1 }} – {{ Math.min(page * perPage, total) }} / {{ total }} bản ghi
      </small>
      <div class="d-flex">
        <b-form-select size="sm" v-model="perPage" :options="optionsPages" @change="changePerPage" class="mr-2"
          style="max-width:100px" />
        <b-pagination v-model="page" :total-rows="total" :per-page="perPage" size="sm" @change="changePage" />
      </div>
    </div>

    <!-- MODAL LOGS -->
    <b-modal ref="logModal" title="Chi tiết log gửi ZNS" size="lg" @hidden="resetLogModal">
      <div v-if="currentBatchId">
        <div class="row mb-3">
          <div class="col-md-3">
            <label>Trạng thái</label>
            <b-form-select v-model="logFilter.status" :options="logStatusOptions" size="sm" />
          </div>
          <div class="col-md-3">
            <label>Từ ngày</label>
            <input type="date" class="form-control form-control-sm" v-model="logFilter.fromDate" />
          </div>
          <div class="col-md-3">
            <label>Đến ngày</label>
            <input type="date" class="form-control form-control-sm" v-model="logFilter.toDate" />
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <b-button size="sm" variant="primary" @click="fetchLogs(1)">Lọc</b-button>
          </div>
        </div>

        <b-table small striped hover :items="logItems" :fields="logFields" head-variant="light">
          <template #cell(status)="d">
            <b-badge :variant="logStatusVariant(d.item.status)">{{ d.item.status }}</b-badge>
          </template>
        </b-table>

        <!-- Phân trang logs -->
        <div class="d-flex justify-content-between align-items-center mt-2" v-if="logTotal > 0">
          <small>Tổng số: {{ logTotal }}</small>
          <b-pagination size="sm" v-model="logPage" :total-rows="logTotal" :per-page="logPerPage" @change="fetchLogs" />
        </div>
        <div v-else class="text-center text-muted py-2">Không có log nào</div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="$refs.logModal.hide()">Đóng</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import apiClient from '@/axios'

export default {
  data() {
    return {
      filter: {
        status: '',
        fromDate: '',
        toDate: ''
      },
      statusOptions: [
        { value: '', text: 'Tất cả' },
        { value: 'RUNNING', text: 'Đang chạy' },
        { value: 'COMPLETED', text: 'Hoàn thành' },
        { value: 'FAILED', text: 'Thất bại' }
      ],
      table: [],
      total: 0,
      page: 1,
      perPage: 20,
      optionsPages: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'status', label: 'Trạng thái' },
        { key: 'progress', label: 'Tiến trình' },
        { key: 'success_count', label: 'Gửi thành công' },
        { key: 'fail_count', label: 'Gửi thất bại' },
        { key: 'created_at', label: 'Thời gian tạo' },
        { key: 'actions', label: 'Thao tác', sortable: false }
      ],

      // Log modal
      currentBatchId: null,
      logFilter: {
        status: '',
        fromDate: '',
        toDate: ''
      },
      logStatusOptions: [
        { value: '', text: 'Tất cả' },
        { value: 'PENDING', text: 'Đang xử lý' },
        { value: 'SUCCESS', text: 'Thành công' },
        { value: 'FAIL', text: 'Thất bại' },
        { value: 'ERROR', text: 'Lỗi' }
      ],
      logItems: [],
      logTotal: 0,
      logPage: 1,
      logPerPage: 20,
      logFields: [
        { key: 'id', label: 'ID' },
        { key: 'customer_id', label: 'Khách hàng' },
        { key: 'phone', label: 'SĐT' },
        { key: 'status', label: 'Trạng thái' },
        { key: 'provider_message_id', label: 'Provider ID' }
      ]
    }
  },

  mounted() {
    this.fetch()
  },

  methods: {
    buildFilter() {
      const f = {}
      if (this.filter.status) f.status = this.filter.status
      if (this.filter.fromDate) f.fromDate = this.filter.fromDate
      if (this.filter.toDate) f.toDate = this.filter.toDate
      return f
    },

    async fetch() {
      try {
        const res = await apiClient.get('/api/v1/admin/customer/zns/batches', {
          params: {
            ...this.buildFilter(),
            currentPage: this.page,
            perPage: this.perPage
          }
        })
        this.table = res.data.items || []
        this.total = res.data.total || 0
      } catch {
        this.$bvToast.toast('Lỗi khi tải lịch sử gửi ZNS', { variant: 'danger', solid: true })
      }
    },

    search() {
      this.page = 1
      this.fetch()
    },

    clear() {
      this.filter = {
        status: '',
        fromDate: '',
        toDate: ''
      }
      this.search()
    },

    changePage(page) {
      this.page = page
      this.fetch()
    },

    changePerPage() {
      this.page = 1
      this.fetch()
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('vi-VN')
    },

    // Log methods
    async viewLogs(batchId) {
      this.currentBatchId = batchId
      this.resetLogFilter()
      this.$refs.logModal.show()
      await this.fetchLogs(1)
    },

    resetLogFilter() {
      this.logFilter = {
        status: '',
        fromDate: '',
        toDate: ''
      }
      this.logPage = 1
      this.logPerPage = 20
    },

    resetLogModal() {
      this.currentBatchId = null
      this.logItems = []
      this.logTotal = 0
    },

    async fetchLogs(page = this.logPage) {
      if (!this.currentBatchId) return

      this.logPage = page
      try {
        const params = {
          status: this.logFilter.status || undefined,
          fromDate: this.logFilter.fromDate || undefined,
          toDate: this.logFilter.toDate || undefined,
          perPage: this.logPerPage,
          currentPage: this.logPage
        }
        const res = await apiClient.get(`/api/v1/admin/customer/zns/batches/${this.currentBatchId}/logs`, { params })
        this.logItems = res.data.items || []
        this.logTotal = res.data.total || 0
      } catch (error) {
        this.$bvToast.toast('Lỗi khi tải log', { variant: 'danger', solid: true })
      }
    },

    logStatusVariant(status) {
      const map = {
        PENDING: 'warning',
        SUCCESS: 'success',
        FAIL: 'danger',
        ERROR: 'dark'
      }
      return map[status] || 'secondary'
    }
  }
}
</script>

<style>
.table-responsive {
  overflow-x: auto;
}
</style>
